import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaTimes, FaShieldAlt, FaFlag } from 'react-icons/fa';

export default function ReportTrackModal({ song, isOpen, onClose, isDarkMode = true }) {
  const navigate = useNavigate();
  const db = getFirestore();

  // Branch Selection: 'route1' (Legal Takedown) or 'route2' (Community Flag)
  const [reportRoute, setReportRoute] = useState('');

  // Form B Fields
  const [issueType, setIssueType] = useState('Uncleared Sample');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !song) return null;

  // Handle Route 1: Legal Owner Takedown Redirect
  const handleProceedRoute1 = () => {
    onClose();
    navigate(`/copyright?trackId=${song.id}`);
  };

  // Handle Route 2: Community & Sample Flagging Submission
  const handleSubmitFormB = async (e) => {
    e.preventDefault();

    if (!details.trim()) {
      toast.error('Please provide details or a timestamp for your report.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Log report into community_flags collection for Admin Verification Queue
      await addDoc(collection(db, 'community_flags'), {
        trackId: song.id,
        trackTitle: song.title || 'Unknown Title',
        artistName: song.artistName || 'Unknown Artist',
        issueType,
        details,
        status: 'pending_review', // Track stays live while admin verifies
        createdAt: serverTimestamp(),
      });

      toast.success('Report submitted. Thank you for helping moderate Atomic!');
      setReportRoute('');
      setDetails('');
      onClose();
    } catch (error) {
      console.error('Error submitting community report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className={`w-full max-w-md p-6 rounded-xl border shadow-2xl transition-all ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}>
        {/* Modal Header */}
        <div className="flex items-center justify-between pb-4 border-b border-zinc-700/50">
          <h3 className="font-bold text-lg flex items-center gap-2">
            
            Report Track
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        {/* Selected Track Preview */}
        <div className="my-4 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/40 flex items-center gap-3">
          {song.albumArtworkURL && (
            <img src={song.albumArtworkURL} alt={song.title} className="w-10 h-10 object-cover rounded" />
          )}
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{song.title}</p>
            <p className="text-xs text-zinc-400 truncate">{song.artistName}</p>
          </div>
        </div>

        {/* Branch Selection Options */}
        <div className="space-y-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
            Select Report Type
          </label>

          {/* Option 1: Copyright Owner */}
          <div
            onClick={() => setReportRoute('route1')}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              reportRoute === 'route1'
                ? 'border-red-500 bg-red-500/10'
                : 'border-zinc-800 hover:border-zinc-700 bg-zinc-800/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <FaShieldAlt className="text-red-500 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">I am the copyright owner or authorized agent</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Submit a formal DMCA / Legal Takedown Notice.
                </p>
              </div>
            </div>
          </div>

          {/* Option 2: Listener / Community Flag */}
          <div
            onClick={() => setReportRoute('route2')}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              reportRoute === 'route2'
                ? 'border-red-500 bg-red-500/10'
                : 'border-zinc-800 hover:border-zinc-700 bg-zinc-800/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <FaShieldAlt className="text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">I am a listener reporting an issue</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Report uncleared samples, uncredited covers, or audio bugs to our team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ROUTE 1: Proceed Action */}
        {reportRoute === 'route1' && (
          <div className="mt-5 pt-4 border-t border-zinc-800 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleProceedRoute1}
              className="px-4 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Continue to Legal Form
            </button>
          </div>
        )}

        {/* ROUTE 2: Form B Fields */}
        {reportRoute === 'route2' && (
          <form onSubmit={handleSubmitFormB} className="mt-5 pt-4 border-t border-zinc-800 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-300 mb-1">
                What issue are you reporting?
              </label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                className="w-full p-2.5 bg-zinc-950 border border-zinc-800 rounded-lg text-sm text-white outline-none focus:border-red-500"
              >
                <option value="Uncleared Sample">Uncleared Sample</option>
                <option value="Uncredited Cover">Uncredited Cover</option>
                <option value="Audio Quality / Bug">Audio Quality / Glitch</option>
                <option value="Other">Other Community Issue</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-zinc-300 mb-1">
                Details & Timestamp *
              </label>
              <textarea
                rows={3}
                required
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="e.g. Contains an uncleared sample from 'Song Name' by 'Artist Name' starting at 01:15"
                className="w-full p-2.5 bg-zinc-950 border border-zinc-800 rounded-lg text-sm text-white outline-none focus:border-red-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}